import { ListGroup, Row, Col, Card, Button, Image } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Message from '../Message';
import Loader from '../Loader';
import { useCreateOrderMutation } from '../../slices/ordersApiSlice';
import { clearCartItems } from '../../slices/cartSlice';
import { setCredentials } from '../../slices/authSlice';
import SectionTitle from './SectionTitle';
import { formatNTD, TAX_RATE, FREE_SHIPPING_THRESHOLD } from '../../utils/pricing';
import { validateShippingContact, DELIVERY_TIME_SLOTS, normalizeMobile } from '../../utils/shippingContact';

// Review + place order. This is the same createOrder call PlaceOrderScreen
// makes — one POST /api/orders — then redirect to the existing OrderScreen
// (/order/:id) where the real PayPal capture happens. Placing an order still
// requires auth, so guests get an inline sign-in link that returns to /store.
const SummarySection = ({ scrollTo, onShippingInvalid }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const { userInfo } = useSelector((state) => state.auth);

  const [createOrder, { isLoading, error }] = useCreateOrderMutation();

  const { cartItems, shippingAddress, shippingMethod, paymentMethod } = cart;

  const shippingComplete = Boolean(
    shippingAddress &&
      shippingAddress.address &&
      shippingAddress.city &&
      shippingAddress.postalCode &&
      shippingAddress.country
  );

  // Mandatory carrier contact (same rules the server re-checks)
  const contactErrors = validateShippingContact({
    email: shippingAddress?.email,
    mobile: shippingAddress?.mobile,
    shippingMethodId: shippingMethod?.id,
    deliveryTime: shippingMethod?.deliveryTime,
  });

  const placeOrderHandler = async () => {
    if (cartItems.length === 0) {
      toast.error('購物車是空的');
      scrollTo?.('cart');
      return;
    }
    if (!shippingMethod?.id) {
      toast.error('請選擇配送方式');
      scrollTo?.('shipping');
      return;
    }
    if (Object.keys(contactErrors).length > 0) {
      toast.error(Object.values(contactErrors)[0]);
      onShippingInvalid?.(); // light up the missing fields in 運送資訊
      scrollTo?.('shipping');
      return;
    }
    if (!shippingComplete) {
      toast.error('請先填寫完整的收件地址');
      onShippingInvalid?.();
      scrollTo?.('shipping');
      return;
    }
    try {
      const res = await createOrder({
        orderItems: cartItems,
        shippingAddress,
        shippingMethod, // server derives the shipping price from this carrier
        paymentMethod,
        itemsPrice: cart.itemsPrice,
        shippingPrice: cart.shippingPrice,
        taxPrice: cart.taxPrice,
        totalPrice: cart.totalPrice,
      }).unwrap();
      // The server saved this mobile to the profile if there was none; mirror
      // that locally so the next checkout (and Profile) are prefilled.
      if (userInfo && !userInfo.mobile) {
        dispatch(setCredentials({ ...userInfo, mobile: normalizeMobile(shippingAddress.mobile) }));
      }
      dispatch(clearCartItems());
      navigate(`/order/${res._id}`);
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  return (
    <>
      <SectionTitle step={5}>確認訂單</SectionTitle>
      <Row>
        <Col md={7}>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h6 className="op-subtitle">運送資訊</h6>
              {shippingComplete ? (
                <p>
                  <strong>地址：</strong>
                  {shippingAddress.address}, {shippingAddress.city}{' '}
                  {shippingAddress.postalCode}, {shippingAddress.country}
                </p>
              ) : (
                <Message variant="warning">
                  尚未填寫收件地址，請於上方填寫。
                </Message>
              )}
              <p className="mb-0">
                <strong>配送：</strong>
                {shippingMethod?.name || (
                  <span className="text-muted">尚未選擇</span>
                )}
              </p>
              {shippingMethod?.id === 'tcat' && (
                <p className="mb-0">
                  <strong>配達時段：</strong>
                  {DELIVERY_TIME_SLOTS[shippingMethod?.deliveryTime] || (
                    <span className="text-muted">尚未選擇</span>
                  )}
                </p>
              )}
              <p className="mb-0" data-testid="summary-contact">
                <strong>聯絡：</strong>
                {shippingAddress?.email || shippingAddress?.mobile ? (
                  [shippingAddress?.email, shippingAddress?.mobile].filter(Boolean).join(' · ')
                ) : (
                  <span className="text-muted">尚未填寫</span>
                )}
              </p>
            </ListGroup.Item>

            <ListGroup.Item>
              <h6 className="op-subtitle">付款方式</h6>
              <strong>方式：</strong>
              {paymentMethod}
            </ListGroup.Item>

            <ListGroup.Item>
              <h6 className="op-subtitle">訂購商品</h6>
              {cartItems.length === 0 ? (
                <Message>購物車是空的</Message>
              ) : (
                <ListGroup variant="flush">
                  {cartItems.map((item, index) => (
                    <ListGroup.Item key={index}>
                      <Row className="align-items-center">
                        <Col xs={2} md={1}>
                          <Image src={item.image} alt={item.name} fluid rounded />
                        </Col>
                        <Col>
                          <Link to={`/product/${item._id}`}>{item.name}</Link>
                        </Col>
                        <Col xs={12} md={4} className="text-md-end">
                          {/* whole NTD: no float noise like 59.970000000000006 */}
                          {item.qty} x {formatNTD(item.price)} ={' '}
                          {formatNTD(item.qty * item.price)}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </ListGroup.Item>
          </ListGroup>
        </Col>

        <Col md={5}>
          {/* Sticky on desktop so the total + Place Order stay in view */}
          <div className="op-sticky">
            <Card>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <h5 className="mb-0">訂單摘要</h5>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col>商品小計：</Col>
                    <Col>{formatNTD(cart.itemsPrice)}</Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col>運費：</Col>
                    <Col data-testid="summary-shipping">
                      {cart.shippingPrice == null ? (
                        <span className="text-muted">請選擇配送方式</span>
                      ) : cart.shippingPrice === 0 ? (
                        `免運（滿 ${formatNTD(FREE_SHIPPING_THRESHOLD)}）`
                      ) : (
                        formatNTD(cart.shippingPrice)
                      )}
                    </Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col>稅金（{Math.round(TAX_RATE * 100)}%）：</Col>
                    <Col data-testid="summary-tax">{formatNTD(cart.taxPrice)}</Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row className="fw-bold">
                    <Col>{cart.shippingPrice == null ? '總計（未含運費）：' : '總計：'}</Col>
                    <Col data-testid="summary-total">{formatNTD(cart.totalPrice)}</Col>
                  </Row>
                </ListGroup.Item>

                {error && (
                  <ListGroup.Item>
                    <Message variant="danger">
                      {error?.data?.message || error.error}
                    </Message>
                  </ListGroup.Item>
                )}

                <ListGroup.Item>
                  {userInfo ? (
                    <Button
                      type="button"
                      variant="success"
                      className="btn-block w-100"
                      disabled={cartItems.length === 0}
                      onClick={placeOrderHandler}
                    >
                      送出訂單
                    </Button>
                  ) : (
                    <Link
                      to="/login?redirect=/store"
                      className="btn btn-primary btn-block w-100"
                    >
                      登入後即可送出訂單
                    </Link>
                  )}
                  {isLoading && <Loader />}
                </ListGroup.Item>
              </ListGroup>
            </Card>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default SummarySection;
